import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import TransferTabs from '../components/TransferTabs';
import QuickActions from '../components/QuickActions';
import TransactionReceiptModal from '../components/TransactionReceiptModal';
import { 
  getUserData, 
  subscribeToAccountUpdates, 
  subscribeToTransactions,
  getRecentTransactions,
  processTransfer,
  updateAccountNumbers
} from '../utils/firestore';
import { getMockTransactions } from '../utils/mockData';

export default function Dashboard() {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allTransactions, setAllTransactions] = useState([]);
  const [isNewUser, setIsNewUser] = useState(false);
  const [copiedAccount, setCopiedAccount] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [receiptTransaction, setReceiptTransaction] = useState(null);
  const showMockRef = useRef(true);

  // Format currency (USD)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Format US account number for display (groups of 4)
  const formatAccountNumber = (number) => {
    if (!number) return '';
    const cleaned = String(number).replace(/\D/g, '');
    if (!cleaned) return '';
    return cleaned.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  };

  // Copy account number to clipboard
  const handleCopyClick = async (accountNumber, accountType) => {
    try {
      await navigator.clipboard.writeText(accountNumber.replace(/\s/g, ''));
      setCopiedAccount(accountType);
      setTimeout(() => setCopiedAccount(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Get current account available balance
  const getPrimaryAccount = (accounts) => {
    if (!Array.isArray(accounts) || accounts.length === 0) return null;
    return (
      accounts.find((acc) => acc.type === 'Current' || acc.type === 'Checking') ||
      accounts.find((acc) => String(acc.type || '').toLowerCase() === 'investment') ||
      accounts[0] ||
      null
    );
  };

  const getPrimaryBalance = (accounts) => {
    const primary = getPrimaryAccount(accounts);
    return primary ? primary.available : 0;
  };

  useEffect(() => {
    if (!currentUser?.uid) return;

    const loadInitialData = async () => {
      try {
        const data = await getUserData(currentUser.uid);
        
        // Update account numbers if needed
        const updatedAccounts = await updateAccountNumbers(currentUser.uid);
        if (updatedAccounts) {
          setAccounts(updatedAccounts);
        } else {
          setAccounts(data.accounts || []);
        }
        
        setUserData(data);
        const investmentUser = (data.accounts || []).some(
          (a) => String(a?.type || '').toLowerCase() === 'investment'
        );
        showMockRef.current = data.showMockTransactions !== false && !investmentUser;

        // Check if user is new (has registrationDate)
        setIsNewUser(!!data.registrationDate);

        // Load transactions once (respects showMockTransactions in getRecentTransactions)
        try {
          const initial = await getRecentTransactions(currentUser.uid);
          setAllTransactions(initial);
        } catch (txErr) {
          const investmentUser = (data.accounts || []).some(
            (a) => String(a?.type || '').toLowerCase() === 'investment'
          );
          if (data.showMockTransactions !== false && !investmentUser) {
            setAllTransactions([...getMockTransactions()].sort((a, b) => new Date(b.date) - new Date(a.date)));
          } else {
            setAllTransactions([]);
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();

    // Subscribe to account updates
    const unsubscribeAccounts = subscribeToAccountUpdates(
      currentUser.uid,
      (updatedAccounts) => setAccounts(updatedAccounts)
    );

    // Subscribe to transactions (real-time). Merge with mock only if user has showMockTransactions on.
    const unsubscribeTransactions = subscribeToTransactions(
      currentUser.uid,
      (realTransactions) => {
        const list = showMockRef.current
          ? [...realTransactions, ...getMockTransactions()]
          : realTransactions;
        const normalized = list
          .map((t) => ({ ...t, date: t.date instanceof Date ? t.date : new Date(t.date) }))
          .sort((a, b) => b.date.getTime() - a.date.getTime());
        setAllTransactions(normalized);
      }
    );

    return () => {
      unsubscribeAccounts();
      unsubscribeTransactions();
    };
  }, [currentUser?.uid]);

  useEffect(() => {
    const investmentUser = (accounts || []).some(
      (a) => String(a?.type || '').toLowerCase() === 'investment'
    );
    showMockRef.current = userData?.showMockTransactions !== false && !investmentUser;
  }, [userData?.showMockTransactions, accounts]);

  const handleTransfer = async (transferData) => {
    try {
      await processTransfer({
        ...transferData,
        userId: currentUser.uid
      });

      // Open proof of payment for the most recent matching transfer
      try {
        const latest = await getRecentTransactions(currentUser.uid);
        const ref = String(transferData?.reference || '').trim();
        const amt = Number(transferData?.amount);
        const match =
          latest.find((t) => {
            const desc = String(t?.description || '');
            const isTransfer = String(t?.category || '').toLowerCase() === 'transfer';
            const isDebit = String(t?.type || '').toLowerCase() === 'debit';
            const amountMatches = Number(t?.amount) === amt;
            const refMatches = ref ? desc.includes(ref) : true;
            return isTransfer && isDebit && amountMatches && refMatches;
          }) || latest[0];

        if (match) setReceiptTransaction(match);
      } catch (_) {
        // If we can't fetch the latest transaction immediately, let the user open it from Recent transactions.
      }
    } catch (error) {
      console.error('Transfer error:', error);
      setErrorMessage('An error occurred while processing your transfer. Please try again or contact support if the issue persists.');
      throw error;
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };

  const getTransactionIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'investment returns':
        return '📈';
      case 'transfer':
        return '↔️';
      case 'bill pay':
        return '💰';
      case 'deposit':
        return '📱';
      default:
        return '💳';
    }
  };

  const isPendingStatus = (status) => String(status || '').toLowerCase() === 'pending';
  const isCompletedStatus = (status) => {
    const s = String(status || '').toLowerCase();
    return !s || s === 'completed';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent" />
          </div>
        </main>
      </div>
    );
  }

  const currentAccount = getPrimaryAccount(accounts);
  const currentBalance = getPrimaryBalance(accounts);
  const routingNumber = String(
    currentAccount?.routingNumber || currentAccount?.sortCode || '021000021'
  ).replace(/\D/g, '');

  const now = new Date();
  const monthCredits = allTransactions.filter(t => {
    const d = new Date(t.date);
    return (
      isCompletedStatus(t.status) &&
      t.type === 'credit' &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear()
    );
  });
  const monthDebits = allTransactions.filter(t => {
    const d = new Date(t.date);
    return (
      isCompletedStatus(t.status) &&
      t.type === 'debit' &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear()
    );
  });
  const monthIn = monthCredits.reduce((s, t) => s + Math.abs(t.amount), 0);
  const monthOut = monthDebits.reduce((s, t) => s + Math.abs(t.amount), 0);
  const pendingTotal = allTransactions
    .filter((t) => isPendingStatus(t.status))
    .reduce((s, t) => s + Math.abs(t.amount), 0);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="min-h-screen bg-slate-50/80 pt-20 sm:pt-24">
      <Navigation />

      {/* Error banner */}
      {errorMessage && (
        <div className="sticky top-16 sm:top-20 z-40 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2">
          <div className="flex items-center justify-between gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3 shadow-sm">
            <p className="text-sm text-red-800 flex-1">{errorMessage}</p>
            <button
              type="button"
              onClick={() => setErrorMessage('')}
              className="shrink-0 p-1 rounded-lg text-red-600 hover:bg-red-100 transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Hero: greeting + single balance + CTA */}
        <section className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="text-slate-500 text-sm sm:text-base">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
              <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-0.5">
                {greeting()}, {userData?.name?.split(' ')[0] || 'User'}
              </h1>
            </div>
            <div className="flex flex-col sm:items-end gap-3">
              <div>
                <p className="text-slate-500 text-xs sm:text-sm">Available balance</p>
                <p className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                  {formatCurrency(currentBalance)}
                </p>
                {pendingTotal > 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    {formatCurrency(pendingTotal)} pending
                  </p>
                )}
              </div>
              <a
                href="#transfer-section"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-primary-700 active:bg-primary-800 transition-colors touch-manipulation"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                Transfer
              </a>
            </div>
          </div>
        </section>

        {/* Account strip: type + number + routing number + copy */}
        <section className="mb-6 sm:mb-8">
          <div className="rounded-2xl bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 p-4 sm:p-5 shadow-lg shadow-primary-900/10 overflow-hidden relative">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.12)_0%,transparent_50%)]" aria-hidden />
            <div className="relative flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-amber-400/25 px-2.5 py-0.5 text-xs font-medium text-amber-200">
                  {currentAccount?.type || 'Account'}
                </span>
                <span className="text-amber-300/60">·</span>
                <span className="text-xs text-amber-100/90">{userData?.name || 'Account holder'}</span>
              </div>
              <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                <div className="flex items-center gap-2">
                  <span className="text-amber-200/95 text-xs sm:text-sm font-medium">Account no.</span>
                  <span className="font-mono text-white text-sm sm:text-base font-medium">{formatAccountNumber(currentAccount?.accountNumber)}</span>
                  <button
                    type="button"
                    onClick={() => handleCopyClick(currentAccount?.accountNumber, 'current')}
                    className="p-1.5 rounded-lg text-white/70 hover:bg-white/20 hover:text-white transition-colors"
                    title="Copy account number"
                  >
                    {copiedAccount === 'current' ? (
                      <span className="text-emerald-300 text-sm">✓</span>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h2m8 0h2a2 2 0 012 2v2m2 4v6a2 2 0 01-2 2h-8a2 2 0 01-2-2v-8z" />
                      </svg>
                    )}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-amber-200/95 text-xs sm:text-sm font-medium">Routing number</span>
                  <span className="font-mono text-white text-sm sm:text-base font-medium">{routingNumber}</span>
                  <button
                    type="button"
                    onClick={() => handleCopyClick(routingNumber, 'routingNumber')}
                    className="p-1.5 rounded-lg text-white/70 hover:bg-white/20 hover:text-white transition-colors"
                    title="Copy routing number"
                  >
                    {copiedAccount === 'routingNumber' ? (
                      <span className="text-emerald-300 text-sm">✓</span>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h2m8 0h2a2 2 0 012 2v2m2 4v6a2 2 0 01-2 2h-8a2 2 0 01-2-2v-8z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Transfer */}
        <section id="transfer-section" className="mb-8">
          <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-slate-100 px-4 sm:px-6 py-3">
              <h2 className="text-base font-semibold text-slate-900">Pay & transfer</h2>
              <p className="text-sm text-slate-500">Between your accounts, to another US bank, or to another Oakvale member</p>
            </div>
            <TransferTabs
              accounts={accounts}
              onTransfer={handleTransfer}
              currentUser={currentUser}
            />
          </div>
        </section>

        {/* Month summary: one compact line */}
        <section className="mb-6 sm:mb-8">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-slate-500">
              {now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <span className="text-slate-300">·</span>
            <span className="text-sm text-green-700 font-medium">+{formatCurrency(monthIn)} in</span>
            <span className="text-slate-300">·</span>
            <span className="text-sm text-red-700 font-medium">−{formatCurrency(monthOut)} out</span>
          </div>
        </section>

        {/* Quick Actions */}
        <section id="quick-actions" className="mb-8">
          <h2 className="text-base font-semibold text-slate-900 mb-3">Quick actions</h2>
          <QuickActions accounts={accounts} />
        </section>

        {/* Recent Transactions */}
        {allTransactions.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-slate-900">Recent transactions</h2>
              <button
                type="button"
                onClick={() => document.getElementById('quick-actions')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                Statements
              </button>
            </div>
            <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden divide-y divide-slate-100">
              {allTransactions.slice(0, 20).map((transaction, index) => (
                <button
                  key={transaction.id || index}
                  type="button"
                  onClick={() => setReceiptTransaction(transaction)}
                  className="w-full text-left px-4 py-3 sm:py-3.5 hover:bg-slate-50/80 transition-colors flex items-center justify-between gap-3 touch-manipulation focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:ring-inset"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 text-base">
                      {getTransactionIcon(transaction.category)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{transaction.description}</p>
                      <p className="text-xs text-slate-500">{formatDate(transaction.date)}</p>
                    </div>
                  </div>
                  {isPendingStatus(transaction.status) ? (
                    <div className="shrink-0 text-right">
                      <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
                        Pending
                      </span>
                      <p className="mt-1 text-xs text-slate-400">
                        {formatCurrency(Math.abs(transaction.amount))}
                      </p>
                    </div>
                  ) : (
                    <p className={`shrink-0 font-medium text-sm sm:text-base ${transaction.type === 'credit' ? 'text-green-600' : 'text-slate-900'}`}>
                      {transaction.type === 'credit' ? '+' : '−'}{formatCurrency(Math.abs(transaction.amount))}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </section>
        )}

        {receiptTransaction && (
          <TransactionReceiptModal
            transaction={receiptTransaction}
            userData={userData}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            onClose={() => setReceiptTransaction(null)}
          />
        )}
      </main>
    </div>
  );
} 